import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
// import Image from '@tiptap/extension-image';
import ResizableImage from '../extensions/ResizableImage';
import Youtube from '@tiptap/extension-youtube';
import {
  Box,
  Paper,
  Stack,
  IconButton,
  Divider,
  Tooltip,
  TextField,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Popover,
  ButtonBase,
} from '@mui/material';
import Dialog from './AppDialog';
import { useDarkMode } from '../App';
import { useDialog } from '../contexts/DialogProvider';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  StrikethroughS,
  FormatListBulleted,
  FormatListNumbered,
  Code,
  FormatQuote,
  Link as LinkIcon,
  Image as ImageIcon,
  PlayCircleOutline,
  FormatClear,
  Highlight as HighlightIcon,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  AddPhotoAlternateOutlined,
  Close,
  RestartAlt,
} from '@mui/icons-material';

// 큐레이션된 글자 색 팔레트 — OS 기본 색상 픽커 대체 (흑백 건반 톤 + 차분한 강조색)
const TEXT_COLORS = [
  '#111111', '#5F5C54', '#8A877E', '#FFFFFF', '#C9A227', '#B0413E', '#C2542B',
  '#B58900', '#3A7D44', '#2E6E8E', '#1D4E89', '#5B4B8A', '#8E4A6F', '#6B4F3A',
];

const formatFileSize = (bytes) =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)}MB`
    : `${Math.max(1, Math.round(bytes / 1024))}KB`;

const MenuBar = ({ editor, darkMode }) => {
  const { toast } = useDialog();
  const [linkDialog, setLinkDialog] = React.useState(false);
  const [linkUrl, setLinkUrl] = React.useState('');
  const [imageDialog, setImageDialog] = React.useState(false);
  const [youtubeDialog, setYoutubeDialog] = React.useState(false);
  const [youtubeUrl, setYoutubeUrl] = React.useState('');
  const [selectedColor, setSelectedColor] = React.useState(darkMode ? '#e0e0e0' : '#111111');
  const [colorAnchor, setColorAnchor] = React.useState(null);
  const [imageFile, setImageFile] = React.useState(null);
  const [imagePreview, setImagePreview] = React.useState(null);
  const [dragOver, setDragOver] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const fileInputRef = React.useRef(null);

  if (!editor) return null;

  const handleAddLink = () => {
    if (linkUrl) {
      editor.chain().focus().extendMarkRange('link').setLink({ href: linkUrl }).run();
      setLinkDialog(false);
      setLinkUrl('');
    }
  };

  const handleFilePicked = (file) => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      toast('이미지 파일만 업로드할 수 있습니다', 'error');
      return;
    }
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const clearImageSelection = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImageFile(null);
    setImagePreview(null);
  };

  const closeImageDialog = () => {
    setImageDialog(false);
    setDragOver(false);
    clearImageSelection();
  };

  const handleInsertImage = async () => {
    if (!imageFile || uploading) return;
    setUploading(true);
    try {
      const api = require('../api/axios').default;
      const formData = new FormData();
      formData.append('image', imageFile);

      const response = await api.post('/upload/image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      editor.chain().focus().setImage({ src: response.data.url }).run();
      closeImageDialog();
    } catch (err) {
      console.error('이미지 업로드 실패:', err);
      toast(err?.response?.data?.message || '이미지 업로드 실패 (최대 5MB)', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleAddYoutube = () => {
    if (youtubeUrl) {
      editor.chain().focus().setYoutubeVideo({ src: youtubeUrl }).run();
      setYoutubeDialog(false);
      setYoutubeUrl('');
    }
  };

  const handleColorChange = (color) => {
    editor.chain().focus().setColor(color).run();
    setSelectedColor(color);
    setColorAnchor(null);
  };

  const handleResetColor = () => {
    editor.chain().focus().unsetColor().run();
    setSelectedColor(darkMode ? '#e0e0e0' : '#111111');
    setColorAnchor(null);
  };

  const ToolbarButton = ({ isActive, onClick, icon: Icon, label, color = 'inherit' }) => (
    <Tooltip title={label}>
      <IconButton
        size="small"
        onClick={onClick}
        color={isActive ? 'primary' : color}
        sx={{
          backgroundColor: isActive ? (darkMode ? 'rgba(250, 250, 247, 0.16)' : 'rgba(17, 17, 17, 0.08)') : 'transparent',
          borderRadius: 1,
          width: 36,
          height: 36,
          '&:hover': {
            backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
          },
        }}
      >
        <Icon sx={{ fontSize: 20 }} />
      </IconButton>
    </Tooltip>
  );

  return (
    <Paper
      sx={{
        backgroundColor: darkMode ? 'grey.800' : '#fafafa',
        borderBottom: darkMode ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid #e0e0e0',
        borderTopLeftRadius: 8,
        borderTopRightRadius: 8,
        p: 1.5,
      }}
    >
      <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 1 }}>
        {/* 텍스트 스타일 */}
        <ToolbarButton
          isActive={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
          icon={FormatBold}
          label="굵게"
        />
        <ToolbarButton
          isActive={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          icon={FormatItalic}
          label="기울임"
        />
        <ToolbarButton
          isActive={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          icon={FormatUnderlined}
          label="밑줄"
        />
        <ToolbarButton
          isActive={editor.isActive('strike')}
          onClick={() => editor.chain().focus().toggleStrike().run()}
          icon={StrikethroughS}
          label="취소선"
        />

        <Divider flexItem orientation="vertical" />

        {/* 제목 */}
        <Tooltip title="제목 1">
          <IconButton
            size="small"
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            sx={{
              backgroundColor: editor.isActive('heading', { level: 1 }) ? (darkMode ? 'rgba(250, 250, 247, 0.16)' : 'rgba(17, 17, 17, 0.08)') : 'transparent',
              borderRadius: 1,
              width: 36,
              height: 36,
              fontSize: '0.85rem',
              fontWeight: 700,
              '&:hover': { backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)' },
            }}
          >
            H1
          </IconButton>
        </Tooltip>
        <Tooltip title="제목 2">
          <IconButton
            size="small"
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            sx={{
              backgroundColor: editor.isActive('heading', { level: 2 }) ? (darkMode ? 'rgba(250, 250, 247, 0.16)' : 'rgba(17, 17, 17, 0.08)') : 'transparent',
              borderRadius: 1,
              width: 36,
              height: 36,
              fontSize: '0.85rem',
              fontWeight: 700,
              '&:hover': { backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)' },
            }}
          >
            H2
          </IconButton>
        </Tooltip>
        <Tooltip title="제목 3">
          <IconButton
            size="small"
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            sx={{
              backgroundColor: editor.isActive('heading', { level: 3 }) ? (darkMode ? 'rgba(250, 250, 247, 0.16)' : 'rgba(17, 17, 17, 0.08)') : 'transparent',
              borderRadius: 1,
              width: 36,
              height: 36,
              fontSize: '0.85rem',
              fontWeight: 700,
              '&:hover': { backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)' },
            }}
          >
            H3
          </IconButton>
        </Tooltip>

        <Divider flexItem orientation="vertical" />

        {/* 리스트 */}
        <ToolbarButton
          isActive={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          icon={FormatListBulleted}
          label="글머리 목록"
        />
        <ToolbarButton
          isActive={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          icon={FormatListNumbered}
          label="번호 목록"
        />

        <Divider flexItem orientation="vertical" />

        {/* 정렬 */}
        <ToolbarButton
          isActive={editor.isActive({ textAlign: 'left' })}
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          icon={FormatAlignLeft}
          label="좌측 정렬"
        />
        <ToolbarButton
          isActive={editor.isActive({ textAlign: 'center' })}
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          icon={FormatAlignCenter}
          label="중앙 정렬"
        />
        <ToolbarButton
          isActive={editor.isActive({ textAlign: 'right' })}
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          icon={FormatAlignRight}
          label="우측 정렬"
        />

        <Divider flexItem orientation="vertical" />

        {/* 색상 */}
        <Tooltip title="글자 색">
          <IconButton
            size="small"
            onClick={(e) => setColorAnchor(e.currentTarget)}
            aria-label="글자 색"
            sx={{
              borderRadius: 1,
              width: 36,
              height: 36,
              '&:hover': { backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)' },
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1 }}>
              <Box component="span" sx={{ fontSize: '0.9rem', fontWeight: 700, color: 'text.primary' }}>A</Box>
              <Box sx={{ width: 15, height: 3, borderRadius: 0.75, bgcolor: selectedColor, mt: '2px', border: selectedColor === '#FFFFFF' ? '0.5px solid' : 'none', borderColor: 'divider' }} />
            </Box>
          </IconButton>
        </Tooltip>
        <Popover
          open={Boolean(colorAnchor)}
          anchorEl={colorAnchor}
          onClose={() => setColorAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          slotProps={{ paper: { sx: { p: 1.75, borderRadius: 3 } } }}
        >
          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            글자 색
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 26px)', gap: 0.75 }}>
            {TEXT_COLORS.map((c) => {
              const isSelected = selectedColor?.toLowerCase() === c.toLowerCase();
              return (
                <ButtonBase
                  key={c}
                  onClick={() => handleColorChange(c)}
                  aria-label={`글자 색 ${c}`}
                  sx={{
                    width: 26,
                    height: 26,
                    borderRadius: 1.5,
                    bgcolor: c,
                    border: '2px solid',
                    borderColor: isSelected ? 'secondary.main' : (c === '#FFFFFF' ? 'divider' : 'transparent'),
                  }}
                />
              );
            })}
          </Box>
          <Divider sx={{ my: 1.25 }} />
          <ButtonBase
            onClick={handleResetColor}
            sx={{ display: 'flex', gap: 1, alignItems: 'center', width: '100%', justifyContent: 'flex-start', borderRadius: 1, px: 0.5, py: 0.5 }}
          >
            <RestartAlt sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">기본 색으로</Typography>
          </ButtonBase>
        </Popover>
        <ToolbarButton
          isActive={editor.isActive('highlight')}
          onClick={() => editor.chain().focus().toggleHighlight().run()}
          icon={HighlightIcon}
          label="하이라이트"
        />

        <Divider flexItem orientation="vertical" />

        {/* 인용문, 코드 */}
        <ToolbarButton
          isActive={editor.isActive('blockquote')}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          icon={FormatQuote}
          label="인용문"
        />
        <ToolbarButton
          isActive={editor.isActive('codeBlock')}
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          icon={Code}
          label="코드"
        />

        <Divider flexItem orientation="vertical" />

        {/* 미디어 */}
        <Tooltip title="링크 추가">
          <IconButton
            size="small"
            onClick={() => setLinkDialog(true)}
            sx={{
              borderRadius: 1,
              width: 36,
              height: 36,
              '&:hover': { backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)' },
            }}
          >
            <LinkIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="이미지 추가">
          <IconButton
            size="small"
            onClick={() => setImageDialog(true)}
            sx={{
              borderRadius: 1,
              width: 36,
              height: 36,
              '&:hover': { backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)' },
            }}
          >
            <ImageIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="YouTube 추가">
          <IconButton
            size="small"
            onClick={() => setYoutubeDialog(true)}
            sx={{
              borderRadius: 1,
              width: 36,
              height: 36,
              '&:hover': { backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)' },
            }}
          >
            <PlayCircleOutline sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>

        <Divider flexItem orientation="vertical" />

        {/* 초기화 */}
        <ToolbarButton
          onClick={() => editor.chain().focus().clearNodes().run()}
          icon={FormatClear}
          label="서식 제거"
        />
      </Stack>

      {/* 링크 다이얼로그 */}
      <Dialog open={linkDialog} onClose={() => setLinkDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>링크 추가</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            fullWidth
            label="URL"
            placeholder="https://example.com"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddLink()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialog(false)}>취소</Button>
          <Button onClick={handleAddLink} variant="contained">
            추가
          </Button>
        </DialogActions>
      </Dialog>

      {/* 이미지 다이얼로그 */}
      <Dialog open={imageDialog} onClose={closeImageDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>이미지 추가</DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => { handleFilePicked(e.target.files?.[0]); e.target.value = ''; }}
          />
          <Box
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFilePicked(e.dataTransfer.files?.[0]); }}
            sx={{
              border: '1.5px dashed',
              borderColor: dragOver ? 'secondary.main' : 'divider',
              borderRadius: 3,
              py: 3.5,
              px: 2,
              textAlign: 'center',
              cursor: 'pointer',
              bgcolor: dragOver
                ? (darkMode ? 'rgba(212, 175, 55, 0.08)' : 'rgba(201, 162, 39, 0.06)')
                : (darkMode ? 'rgba(255, 255, 255, 0.03)' : '#FAFAF7'),
              transition: 'border-color 0.15s ease, background-color 0.15s ease',
            }}
          >
            <AddPhotoAlternateOutlined sx={{ fontSize: 34, color: 'text.secondary' }} />
            <Typography variant="body2" sx={{ mt: 1 }}>이미지를 끌어다 놓으세요</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              JPG · PNG · GIF, 최대 5MB
            </Typography>
            <Button variant="contained" size="small" onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}>
              파일 선택
            </Button>
          </Box>
          {imageFile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: 2.5, px: 1.25, py: 1, mt: 1.25 }}>
              <Box component="img" src={imagePreview} alt="" sx={{ width: 38, height: 38, borderRadius: 2, objectFit: 'cover', flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>{imageFile.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatFileSize(imageFile.size)} · 업로드 준비됨
                </Typography>
              </Box>
              <IconButton size="small" onClick={clearImageSelection} aria-label="선택 취소">
                <Close sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button onClick={closeImageDialog} variant="outlined" color="inherit" sx={{ flex: 1 }}>취소</Button>
          <Button onClick={handleInsertImage} variant="contained" disabled={!imageFile || uploading} sx={{ flex: 2 }}>
            {uploading ? '업로드 중...' : '본문에 삽입'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* YouTube 다이얼로그 */}
      <Dialog open={youtubeDialog} onClose={() => setYoutubeDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>YouTube 추가</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            fullWidth
            label="YouTube URL"
            placeholder="https://www.youtube.com/watch?v=..."
            value={youtubeUrl}
            onChange={(e) => setYoutubeUrl(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddYoutube()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setYoutubeDialog(false)}>취소</Button>
          <Button onClick={handleAddYoutube} variant="contained">
            추가
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default function RichTextEditor({ value, onChange }) {
    const { darkMode } = useDarkMode();
    const editor = useEditor({
    extensions: [
        // StarterKit 3 에는 Link 가 기본 포함(openOnClick: true)이라, 아래 Link 설정과 중복되지 않도록 끈다
        StarterKit.configure({ link: false }),
        // Color 는 textStyle 마크 위에 color 속성을 얹는 방식이라 TextStyle 등록이 필요하다
        TextStyle,
        Color.configure({ types: ['textStyle'] }),
        Highlight.configure({ multicolor: true }),
        Link.configure({ openOnClick: false }),
        TextAlign.configure({ types: ['heading', 'paragraph'] }),
        ResizableImage, // ✅ Image 대신 ResizableImage 사용
        Youtube.configure({
        controls: true,
        nocookie: true,
        }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
        onChange(editor.getHTML());
    },
    });

  return (
    <Box
      sx={{
        border: darkMode ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid #e0e0e0',
        borderRadius: 2,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        height: '600px', // ✅ 고정 높이
        '& .tiptap': {
          '& > *:first-of-type': {
            marginTop: 0,
          },
          '& > *:last-child': {
            marginBottom: 0,
          },
        },
        '& .tiptap-image': {
          maxWidth: '100%',
          height: 'auto',
          borderRadius: 4,
          margin: '12px 0',
        },
        '& iframe': {
          maxWidth: '100%',
          borderRadius: 4,
        },
        '& h1, & h2, & h3, & h4, & h5, & h6': {
          fontWeight: 700,
          marginTop: '16px',
          marginBottom: '8px',
        },
        '& h1': { fontSize: '2rem' },
        '& h2': { fontSize: '1.75rem' },
        '& h3': { fontSize: '1.5rem' },
        '& ul, & ol': {
          marginLeft: '24px',
          marginBottom: '12px',
        },
        '& li': {
          marginBottom: '8px',
        },
        '& blockquote': {
          borderLeft: '3px solid #C9A227',
          paddingLeft: '16px',
          marginLeft: '0',
          marginTop: '12px',
          marginBottom: '12px',
          opacity: 0.8,
        },
        '& code': {
          backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.1)' : '#f5f5f5',
          padding: '2px 6px',
          borderRadius: '3px',
          fontFamily: 'monospace',
          fontSize: '0.9em',
        },
        '& pre': {
          backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.05)' : '#f5f5f5',
          padding: '12px',
          borderRadius: '8px',
          overflowX: 'auto',
          '& code': {
            backgroundColor: 'transparent',
            padding: 0,
          },
        },
      }}
    >
      <MenuBar editor={editor} darkMode={darkMode} />
      {/* ✅ 에디터 콘텐츠 영역 개선 */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          backgroundColor: darkMode ? 'grey.900' : 'background.paper',
          '& .ProseMirror': {
            flex: 1,
            outline: 'none',
            padding: '16px',
            fontFamily: '"Roboto", sans-serif',
            lineHeight: '1.6',
            fontSize: '1rem',
            width: '100%',
            minHeight: '100%',
            color: darkMode ? '#e0e0e0' : 'inherit',
            // ✅ 터치 범위 확대
            WebkitUserSelect: 'text',
            userSelect: 'text',
            WebkitTouchCallout: 'default',
            cursor: 'text',
          }
        }}
      >
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
}